import React, { useEffect, useState } from "react";
import { api, brl, formatDate } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { TrendingUp, Clock, Wallet, Filter, Zap } from "lucide-react";

const PURPLE = "#2B1E4A";

const TYPE_LABEL = {
  COMMISSION_EARNED: { t: "Comissao gerada", c: "text-emerald-600" },
  COMMISSION_REVERSAL: { t: "Estorno", c: "text-red-600" },
  MANUAL_CREDIT: { t: "Credito manual", c: "text-emerald-600" },
  MANUAL_DEBIT: { t: "Debito manual", c: "text-red-600" },
};

export default function EletrosComissoes() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ type: "", date_from: "", date_to: "", client_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const adminCtx = window.location.pathname.startsWith("/admin");
      if (adminCtx) params.scope = "admin";
      const [sum, led] = await Promise.all([
        api.get("/eletros/commissions/summary", { params: adminCtx ? { scope: "admin" } : {} }),
        api.get("/eletros/commissions", { params }),
      ]);
      setSummary(sum.data); setRows(led.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  return (
    <div className="space-y-6 p-4 lg:p-8" data-testid="eletros-comissoes-page">
      <div className="flex items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: "#F5B400" }}><TrendingUp size={24} className="text-[#2B1E4A]" /></div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#F5B400" }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">Comissoes Eletros</h1>
          <p className="text-[12px] text-white/70">{user?.role === "admin" ? "Todas as revendedoras" : "Suas comissoes"}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Sum icon={Clock} label="Comissao a receber (projecao)" value={brl(summary?.commission_projected)} />
        <Sum icon={TrendingUp} label="Comissao gerada" value={brl(summary?.commission_generated)} gold />
        <Sum icon={Wallet} label="Saldo disponivel" value={brl(summary?.available_balance)} gold />
      </div>

      <div className="rounded-2xl border border-border bg-white p-5">
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1 text-sm font-semibold text-brand-dark"><Filter size={15} /> Filtros</div>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="comm-filter-type" className="inp w-auto">
            <option value="">Todos os tipos</option>
            {Object.keys(TYPE_LABEL).map((k) => <option key={k} value={k}>{TYPE_LABEL[k].t}</option>)}
          </select>
          <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="comm-filter-from" className="inp w-auto" />
          <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="comm-filter-to" className="inp w-auto" />
          <button onClick={load} data-testid="comm-apply-filter" className="rounded-xl px-4 py-2 text-sm font-semibold text-white" style={{ background: PURPLE }}>Aplicar</button>
        </div>

        {loading ? <div className="py-8 text-center text-sm text-[#9A8A98]">Carregando...</div>
          : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#D9CFE8] bg-[#FAF8FD] p-8 text-center" data-testid="comm-empty">
              <Zap size={26} className="mx-auto mb-2 text-[#5B2CA0]" />
              <p className="text-sm text-[#6B5A69]">Nenhum lancamento de comissao ainda. Comissoes sao geradas quando o cliente paga.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="comm-ledger">
              {rows.map((r) => {
                const tl = TYPE_LABEL[r.type] || { t: r.type, c: "text-brand-dark" };
                return (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`comm-row-${r.id}`}>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-brand-dark">{r.client_name || "—"}{user?.role === "admin" && r.reseller_name ? ` · ${r.reseller_name}` : ""}</div>
                      <div className="truncate text-[12px] text-[#8A7788]">{r.product_name || "—"}{r.installment_number ? ` · Parcela ${r.installment_number}` : ""} · {formatDate(r.created_at)}</div>
                      {r.base > 0 && <div className="text-[11px] text-[#9A8A98]">Principal recebido {brl(r.base)} · {Math.round((r.rate || 0) * 1000) / 10}%</div>}
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${tl.c}`}>{r.amount >= 0 ? "+ " : "- "}{brl(Math.abs(r.amount))}</div>
                      <div className="text-[10px] font-bold uppercase text-[#9A8A98]">{tl.t}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}

function Sum({ icon: Icon, label, value, gold }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid={`comm-sum-${label}`}>
      <Icon size={18} className={gold ? "text-[#C9960A]" : "text-[#5B2CA0]"} />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">{label}</div>
      <div className="font-display text-lg font-bold" style={{ color: gold ? "#C9960A" : PURPLE }}>{value}</div>
    </div>
  );
}
